import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import type { IExternalFormMultiselectItem } from 'components/ExternalFormMultiselect';
import type { RatioRowData, RatioTypeEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import { useMemo } from 'react';

import { cellRenderCheckbox } from '../cellRenderCheckbox';
import { ratioCollectionGql } from '../uerCoefficientsQuery';

interface IUpdateGroupRatioRowData extends RatioRowData {
    groupRow?: boolean;
}

interface IUseUerCoefficientsQuoteProps {
    onRowCheckedChange: (item: IExternalFormMultiselectItem, isChecked: boolean) => void;
    checkedIds: number[];
    ratioType: RatioTypeEnum;
    changeFilterHandler: (values: any) => void;
    contractId: number;
    hasSubGroupId: boolean;
    isWorkConditionsHasSubGroupId: boolean;
}

export const useUerCoefficientsQuoteColumns = (props: IUseUerCoefficientsQuoteProps) => {
    const {
        onRowCheckedChange,
        checkedIds,
        ratioType,
        contractId,
        changeFilterHandler,
        hasSubGroupId,
        isWorkConditionsHasSubGroupId,
    } = props;

    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Шифр',
                field: 'code',
                autoHeight: true,
                flex: 0,
                width: 130,
                cellRenderer: ({ data }: { data: IUpdateGroupRatioRowData }) => (
                    <div>{data?.groupRow ? '' : data?.code}</div>
                ),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    dataAccessor: 'uerRatioCollection',
                    query: ratioCollectionGql,
                    whereVariables: ['code'],
                    variables: {
                        contractId,
                    },
                    additionalWhereVariables: {
                        ratioType: {
                            eq: ratioType,
                        },
                    },
                    childFieldAccessor: 'code',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'code',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                field: 'workConditions',
                headerName: 'Наименование',
                flex: 1,
                cellClass: 'truncate',
                cellRenderer: ({ value }: { value: string }) => <div>{value || ''}</div>,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    variables: {
                        contractId,
                    },
                    dataAccessor: 'uerRatioCollection',
                    query: ratioCollectionGql,
                    childFieldAccessor: 'workConditions',
                    whereVariables: ['workConditions'],
                    additionalWhereVariables: {
                        ratioType: {
                            eq: ratioType,
                        },
                    },
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'workConditions',
                            checkboxSelection: true,
                            width: 400,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                field: 'kozp',
                headerName: 'К озп',
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 1) || '',
                align: 'center',
                width: 90,
            },
            {
                field: 'kem',
                headerName: 'К эмм',
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 1) || '',
                align: 'center',
                width: 90,
            },
            {
                field: 'ktz',
                headerName: 'К тз озп',
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 1) || '',
                align: 'center',
                width: 102,
            },
            {
                field: 'ktzzpm',
                headerName: 'К тз зпм',
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 1) || '',
                align: 'center',
                width: 102,
            },
            {
                field: 'kmat',
                headerName: 'К мат',
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 1) || '',
                align: 'center',
                width: 90,
            },
            {
                field: 'knr',
                headerName: 'К нр',
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 1) || '',
                align: 'center',
                width: 90,
            },
            {
                field: 'ksp',
                headerName: 'К сп',
                cellRenderer: ({ value }: { value: string }) => formatNumberToRu(value, 1) || '',
                align: 'center',
                width: 90,
            },
        ],
        [changeFilterHandler, contractId, ratioType],
    );

    const resultColumns = useMemo(
        () => [
            {
                headerName: '',
                field: 'checkbox',
                width: 48,
                cellClass: 'firstColumn',
                withoutPadding: true,
                spanHeaderHeight: true,
                cellRenderer: cellRenderCheckbox,
                cellRendererParams: {
                    checkedIds,
                    onRowCheckedChange,
                    ratioType,
                    hasSubGroupId,
                    isWorkConditionsHasSubGroupId,
                },
            },
            ...baseColumns,
        ],

        [baseColumns, checkedIds, hasSubGroupId, isWorkConditionsHasSubGroupId, onRowCheckedChange, ratioType],
    );

    return [resultColumns];
};
